import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Check,
  Receipt,
  User,
  CreditCard,
  Truck,
  MapPin,
  Calendar,
  DollarSign,
  AlertCircle,
  ExternalLink,
  PackageCheck,
  RotateCcw,
  RefreshCw,
  ShoppingBag,
  AlertTriangle,
  FileText,
  ArrowUpRight,
  ArrowDownLeft,
} from "lucide-react";
import * as adminApi from "../../api/adminApi";
import {
  formatDate,
  formatMoney,
  formatOrderStatus,
  formatProductSize,
  formatShipmentStatus,
  formatDeliveryType,
  getOrderStatusBadgeClass,
  isOrderShippable,
  isOrderDeliverable,
  isOrderCancellable,
  isOrderReturnable,
} from "../../utils/format";
import { resolveImageUrl } from "../../utils/images";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";
import AdminExchangeModal from "./AdminExchangeModal";
import "./AdminOrderDetail.css";
import "./AdminOrders.css";
import "../AccountForms.css";
import "../OrderDetail.css";

function formatPaymentStatus(status, isRTL = false) {
  const mapEn = {
    1: "Pending",
    2: "Authorized",
    3: "Captured (Paid)",
    5: "Failed",
    6: "Refunded",
    7: "Awaiting Collection (COD)",
    8: "Voided",
    Pending: "Pending",
    Authorized: "Authorized",
    Captured: "Captured (Paid)",
    Paid: "Captured (Paid)",
    Failed: "Failed",
    Refunded: "Refunded",
    AwaitingCollection: "Awaiting Collection (COD)",
    Voided: "Voided",
  };
  const mapAr = {
    1: "قيد الانتظار",
    2: "معتمد",
    3: "تم التحصيل (مدفوع)",
    5: "فشل الدفع",
    6: "مسترد",
    7: "في انتظار التحصيل (عند الاستلام)",
    8: "ملغي",
    Pending: "قيد الانتظار",
    Authorized: "معتمد",
    Captured: "تم التحصيل (مدفوع)",
    Paid: "تم التحصيل (مدفوع)",
    Failed: "فشل الدفع",
    Refunded: "مسترد",
    AwaitingCollection: "في انتظار التحصيل (عند الاستلام)",
    Voided: "ملغي",
  };
  const map = isRTL ? mapAr : mapEn;
  return map[status] || String(status || "—");
}

function getPaymentBadgeClass(status) {
  const s = String(status);
  if (s === "3" || s === "Captured" || s === "Paid") return "admin-badge--paid";
  if (s === "7" || s === "AwaitingCollection") return "admin-badge--cod";
  if (s === "1" || s === "Pending") return "admin-badge--pending";
  if (s === "2" || s === "Authorized") return "admin-badge--good";
  if (s === "6" || s === "Refunded" || s === "5" || s === "Failed") return "admin-badge--bad";
  return "admin-badge--neutral";
}

function formatPaymentMethod(method, isRTL = false) {
  const mapEn = {
    1: "Credit / Debit Card",
    3: "Cash On Delivery (COD)",
    CreditCard: "Credit / Debit Card",
    CashOnDelivery: "Cash On Delivery (COD)",
  };
  const mapAr = {
    1: "بطاقة ائتمان / خصم مباشر",
    3: "الدفع عند الاستلام (COD)",
    CreditCard: "بطاقة ائتمان / خصم مباشر",
    CashOnDelivery: "الدفع عند الاستلام (COD)",
  };
  const map = isRTL ? mapAr : mapEn;
  return map[method] || String(method || "—");
}

function getAdminShipmentStatusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("delivered") || s === "6") return "admin-badge--delivered";
  if (
    s.includes("transit") ||
    s.includes("route") ||
    s.includes("pickup") ||
    s === "2" ||
    s === "3" ||
    s === "4" ||
    s === "5"
  )
    return "admin-badge--shipped";
  if (s.includes("return") || s === "7") return "admin-badge--bad";
  if (
    s.includes("exception") ||
    s.includes("loss") ||
    s.includes("damage") ||
    s === "8" ||
    s === "11" ||
    s === "12"
  )
    return "admin-badge--bad";
  if (s.includes("cancel") || s.includes("terminat") || s === "9" || s === "10")
    return "admin-badge--bad";
  return "admin-badge--neutral";
}

function getAdminDeliveryTypeMeta(type, isRTL = false) {
  const tStr = String(type || "").toLowerCase();
  if (tStr.includes("return") || type === 25) {
    return {
      label: isRTL ? "شحنة إرجاع" : "Return Pickup",
      icon: RotateCcw,
      badgeClass: "admin-shipment-type-badge--return",
    };
  }
  if (tStr.includes("exchange") || type === 30) {
    return {
      label: isRTL ? "رحلة استبدال" : "Exchange Trip",
      icon: RefreshCw,
      badgeClass: "admin-shipment-type-badge--exchange",
    };
  }
  if (tStr.includes("collection") || type === 15) {
    return {
      label: isRTL ? "تحصيل نقدي" : "Cash Collection",
      icon: DollarSign,
      badgeClass: "admin-shipment-type-badge--collection",
    };
  }
  return {
    label: isRTL ? "شحنة توصيل الطلب" : "Forward Delivery",
    icon: Truck,
    badgeClass: "admin-shipment-type-badge--forward",
  };
}

export default function AdminOrderDetail() {
  const { id } = useParams();
  const { addToast } = useToast();
  const { language, isRTL, t } = useLanguage();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const [cancelModal, setCancelModal] = useState({ open: false, order: null, amount: "" });
  const [lossesModal, setLossesModal] = useState({ open: false, order: null, amount: "" });
  const [returnModal, setReturnModal] = useState({ open: false, order: null, cod: 0, items: [] });
  const [isExchangeModalOpen, setIsExchangeModalOpen] = useState(false);

  const load = () =>
    adminApi
      .fetchAdminOrder(id)
      .then(setOrder)
      .catch((e) => addToast(e.message || "Failed to load order", "error"));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .fetchAdminOrder(id)
      .then((o) => {
        if (!cancelled) setOrder(o);
      })
      .catch((e) => {
        if (!cancelled) addToast(e.message || "Order not found", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const copyToClipboard = (text, message = "Copied to clipboard", key = null) => {
    if (!text) return;
    const str = String(text);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(str).then(() => {
        if (key) {
          setCopiedKey(key);
          setTimeout(() => setCopiedKey(null), 2000);
        }
        addToast(message, "success");
      });
    } else {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = str;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        if (key) {
          setCopiedKey(key);
          setTimeout(() => setCopiedKey(null), 2000);
        }
        addToast(message, "success");
      } catch (err) {
        console.error("Copy failed", err);
      }
    }
  };

  const copyOrderId = () => {
    if (!order?.id) return;
    setCopied(true);
    copyToClipboard(order.id, "Order ID copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const sortedShipments = useMemo(() => {
    if (!order?.shipments) return [];
    return [...order.shipments].sort(
      (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
    );
  }, [order?.shipments]);

  const statusLabel = order ? formatOrderStatus(order.orderStatus, language) : "";
  const orderStatusClass = order ? getOrderStatusBadgeClass(order.orderStatus) : "admin-badge--neutral";
  const paymentStatusLabel = order ? formatPaymentStatus(order.paymentStatus, isRTL) : "";
  const paymentBadgeClass = order ? getPaymentBadgeClass(order.paymentStatus) : "admin-badge--neutral";
  const paymentMethodLabel = order ? formatPaymentMethod(order.paymentMethod, isRTL) : "";

  const showShip = order && isOrderShippable(order.orderStatus);
  const showDeliver = order && isOrderDeliverable(order.orderStatus);
  const showCancel = order && isOrderCancellable(order.orderStatus);
  const showReturn = order && isOrderReturnable(order.orderStatus);

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
      await load();
      addToast("Updated successfully", "success");
    } catch (e) {
      addToast(e.message || "Action failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = (e) => {
    e.preventDefault();
    if (!cancelModal.order) return;
    const amountToRefund = Number(cancelModal.amount);
    const orderId = cancelModal.order.id;
    setCancelModal({ open: false, order: null, amount: "" });

    run(() => adminApi.adminCancelOrder(orderId, amountToRefund));
  };

  const submitLosses = (e) => {
    e.preventDefault();
    if (!lossesModal.order) return;
    const lossesAmount = Number(lossesModal.amount);
    const orderId = lossesModal.order.id;
    setLossesModal({ open: false, order: null, amount: "" });

    run(() => adminApi.adminSetOrderLosses(orderId, lossesAmount));
  };

  const getAvailableQuantity = (orderItem, shipments) => {
    let available = orderItem.quantity;
    if (!shipments) return available;
    shipments.forEach((shipment) => {
      if (shipment.status === "Cancelled") return;
      shipment.shipmentItems?.forEach((sItem) => {
        if (sItem.orderItemId === orderItem.id && sItem.direction === "Inbound") {
          available -= sItem.quantity;
        }
      });
    });
    return available > 0 ? available : 0;
  };

  const handleOpenReturnModal = () => {
    const returnableItems = (order.orderItems || [])
      .map((oi) => ({
        ...oi,
        available: getAvailableQuantity(oi, order.shipments),
        selectedQuantity: 0,
      }))
      .filter((oi) => oi.available > 0);

    setReturnModal({
      open: true,
      order,
      cod: 0,
      items: returnableItems,
    });
  };

  const updateReturnItemQuantity = (itemId, qty) => {
    setReturnModal((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.id === itemId
          ? { ...i, selectedQuantity: Math.min(Math.max(0, qty), i.available) }
          : i
      ),
    }));
  };

  const submitReturn = (e) => {
    e.preventDefault();
    if (!returnModal.order) return;

    const returnItems = returnModal.items
      .filter((i) => i.selectedQuantity > 0)
      .map((i) => ({ orderItemGuid: i.id, quantity: Number(i.selectedQuantity) }));

    if (returnItems.length === 0) {
      addToast("Please select at least one item to return", "error");
      return;
    }

    const orderId = returnModal.order.id;
    const codAmount = Number(returnModal.cod);
    setReturnModal({ open: false, order: null, cod: 0, items: [] });

    run(() => adminApi.adminPartialReturn(orderId, returnItems, codAmount));
  };

  // Financial reconciliation calculations
  const productsTotalPrice = Number(order?.productsTotalPrice || 0);
  const discountAmount = Number(order?.discountAmount || 0);
  const shippingFee = Number(order?.shippingFee || 0);
  const paymentFee = Number(order?.paymentFee || 0);
  const totalPrice = Number(order?.totalPrice || 0);
  const refundedAmount = Number(order?.refundedAmount || 0);
  const losses = Number(order?.losses || 0);

  const netCustomerPaid = Math.max(0, totalPrice - refundedAmount);
  const netRealizedBalance = totalPrice - refundedAmount - losses;

  const totalItemCount = useMemo(() => {
    if (!order?.orderItems) return 0;
    return order.orderItems.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0);
  }, [order?.orderItems]);

  if (loading) {
    return (
      <div className="page-loading">
        <span className="page-loading__dot" />
      </div>
    );
  }

  if (!order && !loading) {
    return (
      <div className="admin-order-detail" style={{ padding: "40px 0" }}>
        <Link to="/admin/orders" className="admin-order-detail__back">
          <ArrowLeft size={16} /> Back to orders
        </Link>
        <div style={{ marginTop: 20 }}>
          <p>Order not found.</p>
        </div>
      </div>
    );
  }

  const ship = order?.shippingAddress;
  const bill = order?.billingAddress;
  const user = order?.user;
  const primaryTx = order?.transactions?.[0];

  return (
    <div className="admin-order-detail">
      {/* Top Navigation */}
      <div className="admin-order-detail__nav">
        <Link to="/admin/orders" className="admin-order-detail__back">
          <ArrowLeft size={16} className={isRTL ? "rtl-flip" : ""} /> {isRTL ? "العودة إلى جميع الطلبات" : "Back to all orders"}
        </Link>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          {isRTL ? "تاريخ الطلب: " : "Order placed on "}<strong>{formatDate(order.createdAt, language)}</strong>
        </div>
      </div>

      {/* Main Order Header */}
      <div className="admin-order-detail__header">
        <div className="admin-order-detail__header-main">
          <div>
            <div className="admin-order-id-row">
              <h1 className="admin-order-title">
                #{String(order.id).slice(0, 8)}
              </h1>
              <button
                type="button"
                className={`admin-copy-btn ${copied ? "admin-copy-btn--copied" : ""}`}
                onClick={copyOrderId}
                title={isRTL ? "نسخ معرف الطلب بالكامل" : "Copy full UUID"}
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                <span>{copied ? (isRTL ? "تم النسخ" : "Copied") : (isRTL ? "نسخ المعرف" : "Copy ID")}</span>
              </button>
            </div>

            <div className="admin-status-strip">
              <span className={`admin-badge ${orderStatusClass}`}>
                {isRTL ? "الطلب: " : "Order: "}{statusLabel}
              </span>
              <span className={`admin-badge ${paymentBadgeClass}`}>
                {isRTL ? "الدفع: " : "Payment: "}{paymentStatusLabel}
              </span>
              <span className="admin-badge admin-badge--neutral">
                {paymentMethodLabel}
              </span>
              {order.completedAt && (
                <span className="admin-badge admin-badge--delivered">
                  {isRTL ? "مكتمل في: " : "Completed: "}{formatDate(order.completedAt, language)}
                </span>
              )}
            </div>
          </div>

          <div style={{ textAlign: isRTL ? "left" : "right" }}>
            <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-muted)" }}>
              {isRTL ? "إجمالي الفاتورة" : "Invoiced Total"}
            </span>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "2rem", fontWeight: 500, color: "var(--text-primary)" }}>
              {formatMoney(totalPrice, language)}
            </div>
          </div>
        </div>

        {/* Action Buttons Bar */}
        <div className="admin-actions-bar">
          {showShip && (
            <button
              type="button"
              className="admin-btn-action admin-btn-action--primary"
              disabled={busy}
              onClick={() => run(() => adminApi.adminMarkShipped(order.id))}
            >
              <Truck size={14} /> {isRTL ? "تعيين كـ جاري الشحن" : "Mark as Shipped"}
            </button>
          )}
          {showDeliver && (
            <button
              type="button"
              className="admin-btn-action admin-btn-action--primary"
              disabled={busy}
              onClick={() => run(() => adminApi.adminMarkDelivered(order.id))}
            >
              <PackageCheck size={14} /> {isRTL ? "تعيين كـ تم التوصيل (مكتمل)" : "Mark as Completed (Delivered)"}
            </button>
          )}
          {showReturn && (
            <>
              <button
                type="button"
                className="admin-btn-action"
                disabled={busy}
                onClick={handleOpenReturnModal}
              >
                <RotateCcw size={14} /> {isRTL ? "إرجاع منتجات" : "Return Items"}
              </button>
              <button
                type="button"
                className="admin-btn-action"
                disabled={busy}
                onClick={() => setIsExchangeModalOpen(true)}
              >
                <RefreshCw size={14} /> {isRTL ? "استبدال منتجات" : "Exchange Items"}
              </button>
            </>
          )}
          <button
            type="button"
            className="admin-btn-action"
            style={{ borderColor: "var(--border)" }}
            onClick={() => setLossesModal({ open: true, order, amount: losses })}
          >
            <DollarSign size={14} />{" "}
            {losses > 0
              ? (isRTL ? "تعديل الخسائر غير المتوقعة" : "Edit Unplanned Losses")
              : (isRTL ? "+ تسجيل خسائر" : "+ Record Losses")}
          </button>
          {showCancel && (
            <button
              type="button"
              className="admin-btn-action admin-btn-action--danger"
              disabled={busy}
              onClick={() => {
                setCancelModal({
                  open: true,
                  order,
                  amount: totalPrice,
                });
              }}
            >
              {isRTL ? "إلغاء الطلب" : "Cancel Order"}
            </button>
          )}
        </div>
      </div>

      {/* Overview Cards Grid */}
      <div className="admin-overview-grid">
        {/* Customer & Account Card */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h3 className="admin-card__title">
              <User size={15} /> Customer Details
            </h3>
            {user?.isGuest ? (
              <span className="admin-badge admin-badge--neutral">Guest</span>
            ) : (
              <span className="admin-badge admin-badge--good">Member</span>
            )}
          </div>
          <div className="admin-card__body">
            {user ? (
              <div>
                <p className="admin-card__primary-text">
                  {[user.firstName, user.lastName].filter(Boolean).join(" ") || "Customer"}
                </p>
                <div className="admin-meta-list">
                  {user.id && (
                    <div className="admin-meta-row">
                      <span>Customer ID:</span>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <strong style={{ fontSize: 11, fontFamily: "monospace" }}>
                          {String(user.id).slice(0, 8)}...
                        </strong>
                        <button
                          type="button"
                          className="admin-copy-icon-btn"
                          onClick={() => copyToClipboard(user.id, "Customer ID copied", "user-id")}
                          title="Copy Full Customer UUID"
                        >
                          {copiedKey === "user-id" ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="admin-meta-row">
                    <span>Email:</span>
                    <strong>
                      <a href={`mailto:${user.email}`} className="admin-link">
                        {user.email || "—"}
                      </a>
                    </strong>
                  </div>
                  <div className="admin-meta-row">
                    <span>Phone:</span>
                    <strong>
                      {user.phoneNumber ? (
                        <a href={`tel:${user.phoneNumber}`} className="admin-link">
                          {user.phoneNumber}
                        </a>
                      ) : (
                        "—"
                      )}
                    </strong>
                  </div>
                  {user.id && (
                    <div style={{ marginTop: 6, paddingTop: 8, borderTop: "1px dashed var(--border)" }}>
                      <Link
                        to={`/admin/orders?userId=${user.id}`}
                        className="admin-link"
                        style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}
                      >
                        <span>View all orders by customer</span>
                        <ExternalLink size={12} />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p style={{ color: "var(--text-muted)" }}>No customer account linked.</p>
            )}
          </div>
        </div>

        {/* Payment & Settlement Card */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h3 className="admin-card__title">
              <CreditCard size={15} /> Payment & Gateway
            </h3>
            <span className={`admin-badge ${paymentBadgeClass}`}>
              {paymentStatusLabel}
            </span>
          </div>
          <div className="admin-card__body">
            <div className="admin-meta-list">
              <div className="admin-meta-row">
                <span>Method:</span>
                <strong>{paymentMethodLabel}</strong>
              </div>
              {primaryTx?.merchantOrderId && (
                <div className="admin-meta-row">
                  <span>Merchant Order:</span>
                  <strong>{primaryTx.merchantOrderId}</strong>
                </div>
              )}
              {primaryTx?.PaymobTransactionId && (
                <div className="admin-meta-row">
                  <span>Paymob Tx ID:</span>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <strong>{primaryTx.PaymobTransactionId}</strong>
                    <button
                      type="button"
                      className="admin-copy-icon-btn"
                      onClick={() =>
                        copyToClipboard(
                          primaryTx.PaymobTransactionId,
                          "Paymob Tx ID copied",
                          "paymob-tx"
                        )
                      }
                      title="Copy Paymob Tx ID"
                    >
                      {copiedKey === "paymob-tx" ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                </div>
              )}
              {primaryTx?.paymobCaptureTransactionId && (
                <div className="admin-meta-row">
                  <span>Capture Tx ID:</span>
                  <strong style={{ fontSize: 12 }}>{primaryTx.paymobCaptureTransactionId}</strong>
                </div>
              )}
              {primaryTx?.intention_order_id && (
                <div className="admin-meta-row">
                  <span>Intention ID:</span>
                  <strong style={{ fontSize: 11 }}>{primaryTx.intention_order_id}</strong>
                </div>
              )}
              {primaryTx?.CompletedAt && (
                <div className="admin-meta-row">
                  <span>Settled At:</span>
                  <strong>{formatDate(primaryTx.CompletedAt)}</strong>
                </div>
              )}
            </div>
            {primaryTx?.errorMessage && (
              <div className="admin-gateway-error-callout">
                <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                <span>{primaryTx.errorMessage}</span>
              </div>
            )}
          </div>
        </div>

        {/* Shipping Address Card */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h3 className="admin-card__title">
              <Truck size={15} /> Shipping Address
            </h3>
          </div>
          <div className="admin-card__body">
            {ship ? (
              <>
                <p className="admin-card__primary-text">
                  {[ship.districtName, ship.cityName, ship.country].filter(Boolean).join(", ")}
                </p>
                {ship.description && <p style={{ margin: "4px 0 0" }}>{ship.description}</p>}
                {ship.phoneNumber && (
                  <p style={{ margin: "6px 0 0", color: "var(--text-muted)" }}>
                    Tel: <a href={`tel:${ship.phoneNumber}`} className="admin-link">{ship.phoneNumber}</a>
                  </p>
                )}
              </>
            ) : (
              <p style={{ color: "var(--text-muted)" }}>No shipping address recorded.</p>
            )}
          </div>
        </div>

        {/* Billing Address Card */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h3 className="admin-card__title">
              <MapPin size={15} /> Billing Address
            </h3>
          </div>
          <div className="admin-card__body">
            {bill ? (
              <>
                <p className="admin-card__primary-text">
                  {[bill.districtName, bill.cityName, bill.country].filter(Boolean).join(", ")}
                </p>
                {bill.description && <p style={{ margin: "4px 0 0" }}>{bill.description}</p>}
                {bill.phoneNumber && (
                  <p style={{ margin: "6px 0 0", color: "var(--text-muted)" }}>
                    Tel: <a href={`tel:${bill.phoneNumber}`} className="admin-link">{bill.phoneNumber}</a>
                  </p>
                )}
              </>
            ) : (
              <p style={{ color: "var(--text-muted)" }}>Same as shipping address.</p>
            )}
          </div>
        </div>
      </div>

      {/* EXECUTIVE FINANCIAL LEDGER */}
      <div className="admin-financial-ledger">
        <div className="admin-financial-ledger__header">
          <h2 className="admin-financial-ledger__title">
            <Receipt size={17} /> Financial Ledger & Reconciliation
          </h2>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Invoice Snapshot at Purchase
          </span>
        </div>

        <div className="ledger-table-wrap">
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Component</th>
                <th>Description / Basis</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {/* 1. Products Total Price */}
              <tr>
                <td>
                  <strong>Products Subtotal</strong>
                </td>
                <td style={{ color: "var(--text-muted)" }}>
                  Catalog value of {totalItemCount} ordered item{totalItemCount === 1 ? "" : "s"}
                </td>
                <td>{formatMoney(productsTotalPrice)}</td>
              </tr>

              {/* 2. Discount Amount */}
              <tr className={discountAmount > 0 ? "ledger-row--discount" : ""}>
                <td>
                  <strong>Discount Amount</strong>
                </td>
                <td style={{ color: "var(--text-muted)" }}>
                  {discountAmount > 0 ? "Coupon & promotional markdown applied" : "No promo discount applied"}
                </td>
                <td>
                  {discountAmount > 0 ? `-${formatMoney(discountAmount)}` : formatMoney(0)}
                </td>
              </tr>

              {/* 3. Shipping Fee */}
              <tr>
                <td>
                  <strong>Shipping Fee</strong>
                </td>
                <td style={{ color: "var(--text-muted)" }}>
                  {shippingFee > 0 ? "Courier delivery logistics charge" : "Free shipping threshold met"}
                </td>
                <td>{formatMoney(shippingFee)}</td>
              </tr>

              {/* 4. Payment Fee */}
              <tr>
                <td>
                  <strong>Payment Fee</strong>
                </td>
                <td style={{ color: "var(--text-muted)" }}>
                  {paymentFee > 0 ? "Payment gateway / COD processing surcharge" : "No gateway surcharge applied"}
                </td>
                <td>{formatMoney(paymentFee)}</td>
              </tr>

              {/* 5. Invoiced Total */}
              <tr className="ledger-row--total">
                <td>
                  <strong>Total Invoiced</strong>
                </td>
                <td style={{ color: "var(--text-muted)" }}>
                  Subtotal − Discount + Shipping + Payment Fee
                </td>
                <td>{formatMoney(totalPrice)}</td>
              </tr>

              {/* 6. Refunded Amount */}
              <tr className={refundedAmount > 0 ? "ledger-row--refund" : ""}>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <strong>Refunded to Customer</strong>
                    {refundedAmount > 0 && (
                      <span className="admin-badge admin-badge--bad" style={{ padding: "2px 8px", fontSize: 10 }}>
                        Refunded
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ color: "var(--text-muted)" }}>
                  {refundedAmount > 0
                    ? "Amount reimbursed to customer via cancellation or return"
                    : "Zero refunds issued"}
                </td>
                <td>
                  {refundedAmount > 0 ? `-${formatMoney(refundedAmount)}` : formatMoney(0)}
                </td>
              </tr>

              {/* 7. Unplanned Losses */}
              <tr className={losses > 0 ? "ledger-row--losses" : ""}>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <strong>Unplanned Losses</strong>
                    {losses > 0 && (
                      <span className="admin-badge admin-badge--bad" style={{ padding: "2px 8px", fontSize: 10 }}>
                        Losses
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ color: "var(--text-muted)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span>
                      {losses > 0
                        ? "Operational shipping loss, courier penalty, or damaged inventory"
                        : "No unplanned losses recorded"}
                    </span>
                    <button
                      type="button"
                      className="admin-link"
                      style={{ fontSize: 12, cursor: "pointer", background: "none", border: "none", padding: 0 }}
                      onClick={() => setLossesModal({ open: true, order, amount: losses })}
                    >
                      {losses > 0 ? "Edit" : "+ Record"}
                    </button>
                  </div>
                </td>
                <td>
                  {losses > 0 ? `-${formatMoney(losses)}` : formatMoney(0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Executive Summary Metrics Strip */}
        <div className="ledger-summary-strip">
          <div className="ledger-summary-card">
            <span>Invoiced Snapshot</span>
            <strong>{formatMoney(totalPrice)}</strong>
          </div>
          <div className="ledger-summary-card ledger-summary-card--net-paid">
            <span>Net Customer Paid</span>
            <strong>{formatMoney(netCustomerPaid)}</strong>
          </div>
          <div className="ledger-summary-card ledger-summary-card--net-realized">
            <span>Net Realized Balance</span>
            <strong>{formatMoney(netRealizedBalance)}</strong>
          </div>
        </div>
      </div>

      {/* ── COURIER SHIPMENTS & LOGISTICS TRIPS HUB ── */}
      <div className="admin-shipments-hub">
        <div className="admin-shipments-hub__header">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div className="admin-shipments-hub__icon-badge">
              <Truck size={22} />
            </div>
            <div>
              <h3 className="admin-shipments-hub__title">
                Courier Shipments & Logistics Trips ({sortedShipments.length})
              </h3>
              <p className="admin-shipments-hub__subtitle">
                Real-time multi-trip tracking, Bosta waybills, package manifests, and delivery attempts
              </p>
            </div>
          </div>
          {sortedShipments.length > 0 && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span className="admin-badge admin-badge--neutral" style={{ fontSize: 11 }}>
                {sortedShipments.filter((s) => s.status === "Delivered" || s.status === 6).length} / {sortedShipments.length} Completed
              </span>
            </div>
          )}
        </div>

        {sortedShipments.length === 0 ? (
          <div className="admin-shipments-empty-card">
            <div className="admin-shipments-empty-card__icon">
              <Truck size={24} />
            </div>
            <h4>No Courier Shipments Dispatched Yet</h4>
            <p>
              This order has not been dispatched to Bosta courier yet. Use the "Mark as Shipped" button in the action toolbar above to schedule pickup and initiate forwarding logistics.
            </p>
          </div>
        ) : (
          <div className="admin-trips-list">
            {sortedShipments.map((shipment, index) => {
              const tripMeta = getAdminDeliveryTypeMeta(shipment.deliveryType, isRTL);
              const TripIcon = tripMeta.icon;
              const typeText = formatDeliveryType(shipment.deliveryType, language);
              const statusText = formatShipmentStatus(shipment.status, language);
              const statusBadgeClass = getAdminShipmentStatusBadgeClass(shipment.status);
              const trackingNum = shipment.trackingNumber;
              const hasFailureReason = Boolean(shipment.lastFailureReason);

              return (
                <div key={shipment.id || index} className="admin-trip-card">
                  <div className="admin-trip-card__head">
                    <div className="admin-trip-card__title-group">
                      <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>
                        Trip #{index + 1}
                      </span>
                      <span className={`admin-trip-badge ${tripMeta.badgeClass}`}>
                        <TripIcon size={13} />
                        <span>{typeText}</span>
                      </span>
                      <span className={`admin-badge ${statusBadgeClass}`}>
                        {statusText}
                      </span>
                      {shipment.attemptCount > 0 && (
                        <span className="admin-attempt-chip" title="Courier delivery attempts">
                          Attempts: {shipment.attemptCount}
                        </span>
                      )}
                    </div>

                    <div className="admin-trip-card__head-actions">
                      {trackingNum && (
                        <a
                          href={`https://bosta.co/en-eg/tracking-shipments?shipment-number=${encodeURIComponent(trackingNum)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-btn-trip admin-btn-trip--track"
                        >
                          <span>Track on Bosta</span>
                          <ExternalLink size={12} />
                        </a>
                      )}
                      {shipment.airwayBillUrl && (
                        <a
                          href={shipment.airwayBillUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-btn-trip admin-btn-trip--awb"
                        >
                          <FileText size={12} />
                          <span>Airway Bill (AWB)</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {hasFailureReason && (
                    <div className="admin-trip-exception-callout">
                      <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                      <div>
                        <strong>Courier Exception / Last Failure Reason:</strong>{" "}
                        {shipment.lastFailureReason}
                      </div>
                    </div>
                  )}

                  <div className="admin-trip-metrics-grid">
                    <div className="admin-trip-metric">
                      <span className="admin-trip-metric__label">Bosta Tracking #</span>
                      <div className="admin-trip-metric__value-wrap">
                        <strong className="admin-trip-code">
                          {trackingNum || "Pending Generation"}
                        </strong>
                        {trackingNum && (
                          <button
                            type="button"
                            className="admin-copy-icon-btn"
                            onClick={() =>
                              copyToClipboard(
                                trackingNum,
                                "Tracking Number copied",
                                `track-${shipment.id}`
                              )
                            }
                            title="Copy Tracking Number"
                          >
                            {copiedKey === `track-${shipment.id}` ? (
                              <Check size={13} />
                            ) : (
                              <Copy size={13} />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {shipment.deliveryId && (
                      <div className="admin-trip-metric">
                        <span className="admin-trip-metric__label">Delivery ID</span>
                        <div className="admin-trip-metric__value-wrap">
                          <span className="admin-trip-code">{shipment.deliveryId}</span>
                          <button
                            type="button"
                            className="admin-copy-icon-btn"
                            onClick={() =>
                              copyToClipboard(
                                shipment.deliveryId,
                                "Delivery ID copied",
                                `deliv-${shipment.id}`
                              )
                            }
                            title="Copy Delivery ID"
                          >
                            {copiedKey === `deliv-${shipment.id}` ? (
                              <Check size={13} />
                            ) : (
                              <Copy size={13} />
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="admin-trip-metric">
                      <span className="admin-trip-metric__label">Package Specs</span>
                      <span className="admin-trip-metric__val">
                        {[shipment.packageSize, shipment.packageType]
                          .filter(Boolean)
                          .join(" · ") || "Standard"}
                      </span>
                    </div>

                    <div className="admin-trip-metric">
                      <span className="admin-trip-metric__label">Trip COD Amount</span>
                      <strong
                        className="admin-trip-metric__val"
                        style={{
                          color:
                            Number(shipment.cashOnDeliveryAmount) > 0
                              ? "var(--gold-dark)"
                              : "inherit",
                        }}
                      >
                        {formatMoney(shipment.cashOnDeliveryAmount || 0, language)}
                      </strong>
                    </div>
                  </div>

                  <div className="admin-trip-milestones">
                    <div className="admin-milestone">
                      <span
                        className={`admin-milestone__dot ${
                          shipment.createdAt ? "admin-milestone__dot--active" : ""
                        }`}
                      />
                      <div className="admin-milestone__info">
                        <span className="admin-milestone__label">{isRTL ? "تم إنشاء الرحلة" : "Trip Created"}</span>
                        <span className="admin-milestone__val">
                          {formatDate(shipment.createdAt, language)}
                        </span>
                      </div>
                    </div>

                    <div className="admin-milestone">
                      <span
                        className={`admin-milestone__dot ${
                          shipment.pickedUpAt ? "admin-milestone__dot--active" : ""
                        }`}
                      />
                      <div className="admin-milestone__info">
                        <span className="admin-milestone__label">{isRTL ? "تم الاستلام" : "Picked Up"}</span>
                        <span className="admin-milestone__val">
                          {shipment.pickedUpAt ? formatDate(shipment.pickedUpAt, language) : (isRTL ? "قيد الانتظار" : "Pending")}
                        </span>
                      </div>
                    </div>

                    <div className="admin-milestone">
                      <span
                        className={`admin-milestone__dot ${
                          shipment.deliveredAt ? "admin-milestone__dot--success" : ""
                        }`}
                      />
                      <div className="admin-milestone__info">
                        <span className="admin-milestone__label">{isRTL ? "تم التوصيل" : "Delivered"}</span>
                        <span className="admin-milestone__val">
                          {shipment.deliveredAt ? formatDate(shipment.deliveredAt, language) : (isRTL ? "قيد الانتظار" : "Pending")}
                        </span>
                      </div>
                    </div>

                    {shipment.returnedAt && (
                      <div className="admin-milestone">
                        <span className="admin-milestone__dot admin-milestone__dot--purple" />
                        <div className="admin-milestone__info">
                          <span className="admin-milestone__label">{isRTL ? "تم الإرجاع" : "Returned"}</span>
                          <span className="admin-milestone__val">
                            {formatDate(shipment.returnedAt, language)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="admin-trip-manifest">
                    <div className="admin-trip-manifest__title">
                      Package Manifest ({shipment.shipmentItems?.length || 0} item
                      {(shipment.shipmentItems?.length || 0) === 1 ? "" : "s"})
                    </div>
                    {shipment.shipmentItems?.length > 0 ? (
                      <div className="admin-trip-items-grid">
                        {shipment.shipmentItems.map((sItem, itemIndex) => {
                          const matchingOi = order.orderItems?.find(
                            (oi) => oi.id === sItem.orderItemId
                          );
                          const name = sItem.productName || matchingOi?.productName || "Product";
                          const color = sItem.color || matchingOi?.color || "";
                          const size = sItem.productSize || matchingOi?.productSize || "";
                          const sku = sItem.code || matchingOi?.code || "—";
                          const imgUrl = sItem.colorImageUrl || matchingOi?.colorImageUrl;
                          const isOutbound =
                            String(sItem.direction || "").toLowerCase() !== "inbound";

                          return (
                            <div key={itemIndex} className="admin-trip-item-card">
                              <div
                                className="admin-trip-item-card__thumb"
                                style={{
                                  backgroundImage: imgUrl
                                    ? `url(${resolveImageUrl(imgUrl)})`
                                    : "none",
                                }}
                              />
                              <div className="admin-trip-item-card__body">
                                <div className="admin-trip-item-card__header">
                                  <span
                                    className="admin-trip-item-card__name"
                                    title={name}
                                  >
                                    <strong>{sItem.quantity}×</strong> {name}
                                  </span>
                                  <span
                                    className={`admin-direction-badge ${
                                      isOutbound
                                        ? "admin-direction-badge--outbound"
                                        : "admin-direction-badge--inbound"
                                    }`}
                                  >
                                    {isOutbound ? (
                                      <ArrowUpRight size={11} />
                                    ) : (
                                      <ArrowDownLeft size={11} />
                                    )}
                                    <span>{isOutbound ? "Outbound" : "Inbound"}</span>
                                  </span>
                                </div>
                                <div className="admin-trip-item-card__sub">
                                  <span>SKU: {sku}</span>
                                  {size && <span>· {formatProductSize(size, language)}</span>}
                                  {color && <span>· {color}</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
                        {isRTL ? "لا توجد منتجات مسجلة في بيان هذه الشحنة." : "No package items tracked in this trip manifest."}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ordered Products Table */}
      <div className="admin-items-card">
        <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-input)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, color: "var(--gold-dark)", display: "flex", alignItems: "center", gap: 8 }}>
            <ShoppingBag size={16} /> {isRTL ? "منتجات الطلب" : "Order Items"} ({order.orderItems?.length || 0})
          </h3>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {isRTL ? "إجمالي الكمية: " : "Total Qty: "}{totalItemCount}
          </span>
        </div>

        <table className="admin-items-table">
          <thead>
            <tr>
              <th>{isRTL ? "المنتج" : "Product"}</th>
              <th>{isRTL ? "رمز المنتج" : "SKU"}</th>
              <th>{isRTL ? "المقاس" : "Size"}</th>
              <th>{isRTL ? "اللون" : "Color"}</th>
              <th style={{ textAlign: "center" }}>{isRTL ? "الكمية" : "Quantity"}</th>
              <th style={{ textAlign: isRTL ? "left" : "right" }}>{isRTL ? "سعر القطعة" : "Unit Price"}</th>
              <th style={{ textAlign: isRTL ? "left" : "right" }}>{isRTL ? "الإجمالي" : "Line Total"}</th>
            </tr>
          </thead>
          <tbody>
            {(order.orderItems || []).map((item) => {
              const lineTotal = Number(item.price || 0) * Number(item.quantity || 1);
              return (
                <tr key={item.id}>
                  <td>
                    <div className="admin-item-product-cell">
                      <div
                        className="admin-item-thumb"
                        style={{
                          backgroundImage: item.colorImageUrl
                            ? `url(${resolveImageUrl(item.colorImageUrl)})`
                            : "none",
                        }}
                      />
                      <div className="admin-item-info">
                        <span className="admin-item-name">{item.productName}</span>
                      </div>
                    </div>
                  </td>
                  <td style={{ color: "var(--text-muted)", fontSize: 12 }}>
                    {item.code || "—"}
                  </td>
                  <td>{formatProductSize(item.productSize, language) || "—"}</td>
                  <td>{item.color || "—"}</td>
                  <td style={{ textAlign: "center", fontWeight: 600 }}>×{item.quantity}</td>
                  <td style={{ textAlign: isRTL ? "left" : "right" }}>{formatMoney(item.price, language)}</td>
                  <td style={{ textAlign: isRTL ? "left" : "right", fontWeight: 600 }}>
                    {formatMoney(lineTotal, language)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* CANCEL MODAL */}
      {cancelModal.open && cancelModal.order && (
        <div className="admin-modal-overlay" role="dialog">
          <div className="admin-modal">
            <h3>{isRTL ? "إلغاء الطلب" : "Cancel Order"}</h3>
            <p className="admin-modal__hint">
              {isRTL
                ? `إلغاء الطلب #${String(cancelModal.order.id).slice(0, 8)}. يرجى تحديد المبلغ المراد استرداده.`
                : `Cancel order #${String(cancelModal.order.id).slice(0, 8)}. Please specify the refund amount.`}
            </p>
            <form onSubmit={submitCancel}>
              <div className="auth-field">
                <label>{isRTL ? "مبلغ الاسترداد (ج.م)" : "Refund Amount (EGP)"}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={totalPrice}
                  value={cancelModal.amount}
                  onChange={(e) => setCancelModal({ ...cancelModal, amount: e.target.value })}
                  required
                />
                <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                  {isRTL ? "الحد الأقصى للاسترداد: " : "Maximum refundable amount: "}{formatMoney(totalPrice, language)}
                </p>
              </div>
              <div className="admin-modal__actions">
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setCancelModal({ open: false, order: null, amount: "" })}
                >
                  {isRTL ? "إغلاق" : "Close"}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ backgroundColor: "#8b4545", borderColor: "#8b4545", color: "white" }}
                >
                  {isRTL ? "تأكيد الإلغاء" : "Confirm Cancellation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* LOSSES MODAL */}
      {lossesModal.open && lossesModal.order && (
        <div className="admin-modal-overlay" role="dialog">
          <div className="admin-modal">
            <h3>{isRTL ? "تسجيل الخسائر التشغيلية" : "Record Unplanned Losses"}</h3>
            <p className="admin-modal__hint">
              {isRTL
                ? `تسجيل أو تحديث الخسائر التشغيلية غير المتوقعة (مثل غرامات الشحن، المنتجات التالفة) للطلب #${String(lossesModal.order.id).slice(0, 8)}.`
                : `Record or update unplanned financial losses (e.g. shipping penalties, damaged goods) for order #${String(lossesModal.order.id).slice(0, 8)}.`}
            </p>
            <form onSubmit={submitLosses}>
              <div className="auth-field">
                <label>{isRTL ? "مبلغ الخسارة (ج.م)" : "Loss Amount (EGP)"}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={lossesModal.amount}
                  onChange={(e) => setLossesModal({ ...lossesModal, amount: e.target.value })}
                  required
                />
              </div>
              <div className="admin-modal__actions">
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setLossesModal({ open: false, order: null, amount: "" })}
                >
                  {isRTL ? "إغلاق" : "Close"}
                </button>
                <button type="submit" className="btn-primary">
                  {isRTL ? "حفظ الخسائر" : "Save Losses"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PARTIAL RETURN MODAL */}
      {returnModal.open && returnModal.order && (
        <div className="admin-modal-overlay" role="dialog">
          <div className="admin-modal" style={{ maxWidth: 650 }}>
            <h3>{isRTL ? "إرجاع جزئي للطلب" : "Partial Return"}</h3>
            <p className="admin-modal__hint">
              {isRTL
                ? `حدد المنتجات المراد إرجاعها للطلب #${String(returnModal.order.id).slice(0, 8)}.`
                : `Select items to return for order #${String(returnModal.order.id).slice(0, 8)}.`}
            </p>
            {returnModal.items.length === 0 ? (
              <p style={{ color: "var(--text-muted)", marginBottom: 20 }}>
                {isRTL
                  ? "لا توجد منتجات قابلة للإرجاع. جميع المنتجات تم إرجاعها بالفعل أو قيد المعالجة."
                  : "No returnable items available. All items are either already returned or processing return."}
              </p>
            ) : (
              <form onSubmit={submitReturn}>
                <div
                  style={{
                    maxHeight: 300,
                    overflowY: "auto",
                    marginBottom: 20,
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius)",
                    background: "var(--surface-card)",
                  }}
                >
                  <table className="shipment-table">
                    <thead style={{ position: "sticky", top: 0, backgroundColor: "var(--surface-input)", zIndex: 1 }}>
                      <tr>
                        <th>{isRTL ? "المنتج" : "Item"}</th>
                        <th>{isRTL ? "السعر" : "Price"}</th>
                        <th>{isRTL ? "المتاح" : "Available"}</th>
                        <th style={{ width: 100 }}>{isRTL ? "كمية الإرجاع" : "Return Qty"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {returnModal.items.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 4,
                                  background: item.colorImageUrl
                                    ? `url(${resolveImageUrl(item.colorImageUrl)}) center/cover`
                                    : "var(--surface-input)",
                                }}
                              />
                              <div>
                                <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>
                                  {item.productName}
                                </div>
                                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                  {[
                                    item.code && `SKU: ${item.code}`,
                                    formatProductSize(item.productSize, language),
                                    item.color,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ color: "var(--text-primary)" }}>{formatMoney(item.price, language)}</td>
                          <td style={{ color: "var(--text-secondary)" }}>{item.available}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max={item.available}
                              value={item.selectedQuantity}
                              onChange={(e) =>
                                updateReturnItemQuantity(item.id, parseInt(e.target.value) || 0)
                              }
                              style={{
                                width: "100%",
                                padding: "6px 8px",
                                background: "var(--surface-input)",
                                color: "var(--text-primary)",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                textAlign: "center",
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="auth-field">
                  <label>{isRTL ? "مبلغ الاسترداد عند الاستلام (COD)" : "Refund (COD) Amount"}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={returnModal.cod}
                    onChange={(e) => setReturnModal({ ...returnModal, cod: e.target.value })}
                    required
                  />
                  <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 0 0" }}>
                    {isRTL
                      ? "أدخل المبلغ الذي يجب على المندوب تسليمه نقداً للعميل (أو 0)."
                      : "Enter the amount the courier should hand to the customer (or 0)."}
                  </p>
                </div>

                <div className="admin-modal__actions">
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => setReturnModal({ open: false, order: null, cod: 0, items: [] })}
                  >
                    {isRTL ? "إلغاء" : "Cancel"}
                  </button>
                  <button type="submit" className="btn-primary">
                    {isRTL ? "تأكيد الإرجاع" : "Confirm Return"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* EXCHANGE MODAL */}
      {isExchangeModalOpen && order && (
        <AdminExchangeModal
          isOpen={isExchangeModalOpen}
          order={order}
          onClose={() => setIsExchangeModalOpen(false)}
          onSuccess={load}
        />
      )}
    </div>
  );
}
